import ReactDOM from 'react-dom/client';
import { Widget } from './components/Widget';
import styles from './styles.scss?inline';
import { fetchWidget, checkUserEligibility, authenticateWithOtp, type IWidgetConfig} from './api/apiClient';
import { loadEnv } from './configs/env';

// Host pages embed this widget alongside their own styles, so it must not
// leak CSS out (and shouldn't be clobbered by host CSS either). Two layers of
// isolation: a shadow root per host element (styles.scss injected once,
// scoped to that shadow tree only), plus every selector in styles.scss is
// SCSS-nested under #minimal-widget-root, so nothing ever compiles to a bare
// `button`/`*` even if this stylesheet ends up somewhere without a shadow root.
const ROOT_ID = 'minimal-widget-root';

function getShadowMount(host: HTMLElement): HTMLElement {
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });

  let mount = shadow.getElementById(ROOT_ID);
  if (!mount) {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    shadow.appendChild(styleElement);

    mount = document.createElement('div');
    mount.id = ROOT_ID;
    shadow.appendChild(mount);
  }

  return mount;
}

interface InitResult {
  root: ReactDOM.Root;
  config: IWidgetConfig;
}

// Common initialization logic used by both integration methods
async function initializeWidget(
  widgetId: string | null,
  playerToken: string | null,
  container: HTMLElement,
  role: 'player' | 'admin' = 'player',
  lang: string = 'en',
  progressMode: 'cumulative' | 'step' = 'cumulative'
): Promise<InitResult | null> {
  try {
    await loadEnv();

    // In admin mode without widget-id, render with empty config
    if (role === 'admin' && !widgetId) {
      console.log('Admin mode: No widget-id, rendering with empty config');
      const config = {} as IWidgetConfig;
      const root = ReactDOM.createRoot(getShadowMount(container));
      root.render(<Widget config={config} lang={lang} progressMode={progressMode} />);
      return { root, config };
    }

    // Fetch widget config using the API client
    const data = (await fetchWidget(widgetId!)).data;
    const config = JSON.parse(data) as IWidgetConfig;

    // Authenticate only if player-token is provided
    if (playerToken) {
      await authenticateWithOtp(playerToken, config.promotionId);
    } else if (role === 'admin') {
      console.log('Admin mode: No player-token, skipping authentication');
    }

    console.log('Fetched widget config:', config);

    // Check eligibility only if player-token is provided
    let eligibility = { isEligible: true };
    if (playerToken) {
      eligibility = await checkUserEligibility(config.promotionId);
    }

    // In admin mode, render regardless of eligibility
    if (!eligibility.isEligible && role === 'player') {
      console.log('Player not eligible for this widget');
      return null;
    }

    if (!eligibility.isEligible && role === 'admin') {
      console.log('Admin mode: Player not eligible, but rendering anyway');
    }

    const root = ReactDOM.createRoot(getShadowMount(container));
    root.render(<Widget config={config} lang={lang} progressMode={progressMode} />);
    return { root, config };
  } catch (error) {
    console.error('Widget initialization error:', error);

    // In admin mode, render widget even if initialization fails
    if (role === 'admin') {
      console.log('Admin mode: Rendering widget despite initialization error');
      const config = {} as IWidgetConfig;
      const root = ReactDOM.createRoot(getShadowMount(container));
      root.render(<Widget config={config} lang={lang} progressMode={progressMode} />);
      return { root, config };
    }

    return null;
  }
}

// Custom element for new integration method
class MinimalWidget extends HTMLElement {
  private root: ReactDOM.Root | null = null;
  private currentConfig: IWidgetConfig | null = null;
  private isInitializing = false;
  private initializedWith: { widgetId: string | null; playerToken: string | null; role: string } | null = null;

  static get observedAttributes() {
    return ['widget-id', 'player-token', 'role', 'lang', 'progress-mode'];
  }

  connectedCallback() {
    // Called when element is inserted into DOM
    this.initWidget();
  }

  disconnectedCallback() {
    // Called when element is removed from DOM
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    this.currentConfig = null;
    this.initializedWith = null;
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    // Called when observed attributes change
    if (oldValue !== newValue && this.isConnected) {
      if (name === 'lang' || name === 'progress-mode') {
        // Lightweight re-render, no full re-init (re-fetch/re-auth) needed
        this.updateDisplayProps();
      } else {
        this.initWidget();
      }
    }
  }

  private updateDisplayProps() {
    if (this.root && this.currentConfig) {
      const lang = this.getAttribute('lang') || 'en';
      const progressMode = this.getAttribute('progress-mode') === 'step' ? 'step' : 'cumulative';
      this.root.render(<Widget config={this.currentConfig} lang={lang} progressMode={progressMode} />);
    }
  }

  private async initWidget() {
    const widgetId = this.getAttribute('widget-id');
    const playerToken = this.getAttribute('player-token');
    const role = this.getAttribute('role') || 'player';
    const lang = this.getAttribute('lang') || 'en';
    const progressMode = this.getAttribute('progress-mode') === 'step' ? 'step' : 'cumulative';

    // Validation based on role
    if (role === 'admin') {
      // Admin role: no required attributes (can work without widget-id or player-token)
      // Will render with whatever data is available
    } else {
      // Player role: both widget-id and player-token are required
      if (!widgetId || !playerToken) {
        console.error('MinimalWidget (player): missing required attributes (widget-id, player-token)');
        return;
      }
    }

    // Prevent duplicate initializations with the same values
    if (
      this.initializedWith &&
      this.initializedWith.widgetId === widgetId &&
      this.initializedWith.playerToken === playerToken &&
      this.initializedWith.role === role
    ) {
      return;
    }

    // Prevent concurrent initializations
    if (this.isInitializing) {
      return;
    }

    this.isInitializing = true;
    console.log('Initializing MinimalWidget with:', { widgetId, playerToken, role, lang });

    // Clear any existing content
    if (this.root) {
      this.root.unmount();
      this.root = null;
      this.currentConfig = null;
    }

    // Initialize widget and render into this element (not body)
    const result = await initializeWidget(widgetId, playerToken, this, role as 'player' | 'admin', lang, progressMode);
    if (result) {
      this.root = result.root;
      this.currentConfig = result.config;
    }

    // Mark as initialized with these values
    this.initializedWith = { widgetId, playerToken, role };
    this.isInitializing = false;
  }
}

// Register the custom element
if (typeof customElements !== 'undefined' && !customElements.get('minimal-widget')) {
  customElements.define('minimal-widget', MinimalWidget);
}

// Parse data attributes from script tag (backward compatibility)
export async function initBalanceWidget() {
  const scripts = document.querySelectorAll('script[data-balance-widget]');

  for (const script of scripts) {
    const widgetId = script.getAttribute('data-widget-id');
    const playerToken = script.getAttribute('data-player-token');
    const lang = script.getAttribute('data-lang') || 'en';
    const progressMode = script.getAttribute('data-progress-mode') === 'step' ? 'step' : 'cumulative';
    console.log('Initializing Balance Widget with:', { widgetId, playerToken, lang, progressMode });

    if (!widgetId || !playerToken) {
      console.error('Balance Widget: missing required data attributes');
      continue;
    }

    // Stable container ID — prevents re-init on every SPA navigation / re-injection
    const containerId = `balance-widget-root-${widgetId}`;
    if (document.getElementById(containerId)) continue;

    const container = document.createElement('div');
    container.id = containerId;
    document.body.appendChild(container);

    // Reuse common initialization logic
    await initializeWidget(widgetId, playerToken, container, 'player', lang, progressMode);

    // Keep the container alive if the SPA framework replaces body children
    const observer = new MutationObserver(() => {
      if (!document.body.contains(container)) {
        document.body.appendChild(container);
      }
    });
    observer.observe(document.body, { childList: true });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBalanceWidget);
} else {
  initBalanceWidget();
}
